package com.example.socialmediaapp.ui.screens

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.example.socialmediaapp.network.ApiUser
import com.example.socialmediaapp.network.AuthRepository
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.MutableStateFlow

@OptIn(ExperimentalMaterial3Api::class, FlowPreview::class)
@Composable
fun SearchScreen(
    authRepository: AuthRepository? = null,
    onBack: () -> Unit,
    onUserClick: (userId: String) -> Unit = {},
    // Legacy params kept for compatibility
    onSearch: ((String) -> kotlinx.coroutines.flow.Flow<List<com.example.socialmediaapp.data.models.Message>>)? = null,
    onResultClick: (chatId: String, messageId: String) -> Unit = { _, _ -> }
) {
    var searchQuery by remember { mutableStateOf("") }
    var results by remember { mutableStateOf<List<ApiUser>>(emptyList()) }
    var isLoading by remember { mutableStateOf(false) }
    var errorMsg by remember { mutableStateOf<String?>(null) }

    // Debounced search
    val queryFlow = remember { MutableStateFlow("") }
    LaunchedEffect(Unit) {
        queryFlow
            .debounce(400)
            .distinctUntilChanged()
            .collect { q ->
                if (q.isBlank() || authRepository == null) {
                    results = emptyList()
                    return@collect
                }
                isLoading = true
                errorMsg = null
                authRepository.searchUsers(q).fold(
                    onSuccess = { results = it },
                    onFailure = { errorMsg = "Search failed" }
                )
                isLoading = false
            }
    }

    LaunchedEffect(searchQuery) { queryFlow.value = searchQuery }

    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    TextField(
                        value = searchQuery,
                        onValueChange = { searchQuery = it },
                        placeholder = { Text("Search people") },
                        modifier = Modifier.fillMaxWidth(),
                        colors = TextFieldDefaults.colors(
                            focusedIndicatorColor = androidx.compose.ui.graphics.Color.Transparent,
                            unfocusedIndicatorColor = androidx.compose.ui.graphics.Color.Transparent
                        ),
                        singleLine = true,
                        trailingIcon = {
                            if (searchQuery.isNotEmpty()) {
                                IconButton(onClick = { searchQuery = "" }) {
                                    Icon(Icons.Default.Clear, "Clear")
                                }
                            }
                        }
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, "Back")
                    }
                }
            )
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            when {
                searchQuery.isBlank() -> {
                    Column(
                        modifier = Modifier.align(Alignment.Center),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Icon(
                            Icons.Default.Search,
                            contentDescription = null,
                            modifier = Modifier.size(64.dp),
                            tint = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Spacer(Modifier.height(16.dp))
                        Text("Search for people", style = MaterialTheme.typography.titleMedium)
                    }
                }
                isLoading -> {
                    CircularProgressIndicator(modifier = Modifier.align(Alignment.Center))
                }
                errorMsg != null -> {
                    Text(
                        errorMsg!!,
                        modifier = Modifier.align(Alignment.Center),
                        color = MaterialTheme.colorScheme.error
                    )
                }
                results.isEmpty() -> {
                    Text(
                        "No users found for \"$searchQuery\"",
                        modifier = Modifier.align(Alignment.Center),
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                else -> {
                    LazyColumn(modifier = Modifier.fillMaxSize()) {
                        items(results, key = { it.id }) { user ->
                            ListItem(
                                headlineContent = { Text(user.username) },
                                supportingContent = user.bio?.let { { Text(it, maxLines = 1) } },
                                leadingContent = {
                                    if (user.avatarUrl != null) {
                                        AsyncImage(
                                            model = user.avatarUrl,
                                            contentDescription = null,
                                            modifier = Modifier
                                                .size(40.dp)
                                                .clip(CircleShape)
                                        )
                                    } else {
                                        Icon(
                                            Icons.Default.AccountCircle,
                                            contentDescription = null,
                                            modifier = Modifier.size(40.dp)
                                        )
                                    }
                                },
                                modifier = Modifier.clickable { onUserClick(user.id) }
                            )
                            HorizontalDivider()
                        }
                    }
                }
            }
        }
    }
}
